import { overlay } from 'overlay-kit';
import { useState } from 'react';

import { Button } from '@/shared/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

type DeletePromptLibraryDialogProps = {
  isOpen: boolean;
  close: (value: boolean) => void;
  onConfirm?: () => Promise<boolean>;
  promptTitle: string;
  unmount: () => void;
};

function DeletePromptLibraryDialog({
  isOpen,
  close,
  onConfirm,
  promptTitle,
  unmount,
}: DeletePromptLibraryDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (isDeleting) {
      return;
    }

    if (!onConfirm) {
      close(true);
      return;
    }

    setIsDeleting(true);

    try {
      const isDeleted = await onConfirm();

      if (isDeleted) {
        close(true);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        if (!isDeleting) {
          close(false);
        }
      }}
      onExitComplete={unmount}
    >
      <DialogContent size="s">
        <DialogHeader>
          <DialogTitle>Delete this prompt?</DialogTitle>
          <DialogDescription>
            &quot;{promptTitle}&quot; will be permanently deleted and cannot be
            recovered.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div />
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={isDeleting}
              onClick={() => close(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              isLoading={isDeleting}
              onClick={() => {
                void handleDelete();
              }}
            >
              Delete prompt
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function openDeletePromptLibraryDialog({
  promptTitle,
  onConfirm,
}: {
  promptTitle: string;
  onConfirm: () => Promise<boolean>;
}) {
  return overlay.openAsync<boolean>(({ isOpen, close, unmount }) => (
    <DeletePromptLibraryDialog
      isOpen={isOpen}
      close={close}
      onConfirm={onConfirm}
      promptTitle={promptTitle}
      unmount={unmount}
    />
  ));
}
